import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  getCollections,
  deleteCollection,
  toggleCollectionStatus,
  reorderCollections
} from '../services/collectionService';
import { Collection } from '../types/collection';
import useToastStore from '../store/useToastStore';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Package,
  GripVertical,
  Sparkles,
  ArrowUpDown
} from 'lucide-react';

const CollectionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);

  const { showToast } = useToastStore();

  // Load collections
  const loadCollections = async () => {
    try {
      setIsLoading(true);
      const data = await getCollections();
      setCollections(data);
    } catch (error) {
      console.error('Error loading collections:', error);
      showToast('Failed to load collections', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  // Navigation handlers
  const handleCreateCollection = () => {
    navigate('/collections/add');
  };

  const handleEditCollection = (collectionId: string) => {
    navigate(`/collections/edit/${collectionId}`);
  };

  const handleDelete = async (collectionId: string) => {
    if (!collectionId) {
      console.error('No collection ID provided for deletion');
      showToast('Invalid collection ID', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('Attempting to delete collection:', collectionId);
      await deleteCollection(collectionId);
      showToast('Collection deleted successfully!', 'success');
      loadCollections();
    } catch (error) {
      console.error('Error deleting collection:', error);
      showToast('Failed to delete collection', 'error');
    }
  };

  const handleToggleStatus = async (collection: Collection) => {
    try {
      await toggleCollectionStatus(collection._id, !collection.enabled);
      showToast(`Collection ${!collection.enabled ? 'enabled' : 'disabled'} successfully!`, 'success');
      loadCollections();
    } catch (error) {
      console.error('Error toggling collection status:', error);
      showToast('Failed to update collection status', 'error');
    }
  };

  // Drag and drop handler
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(collections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UX
    setCollections(items);

    try {
      setIsReordering(true);
      
      // Prepare reorder data with new order values
      const reorderData = items.map((collection, index) => ({
        id: collection._id,
        order: index + 1
      }));

      await reorderCollections(reorderData);
      showToast('Collections reordered successfully!', 'success');
    } catch (error) {
      console.error('Error reordering collections:', error);
      showToast('Failed to reorder collections', 'error');
      // Revert to original order on error
      loadCollections();
    } finally {
      setIsReordering(false);
    }
  };

  if (isLoading) {
  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg w-1/4 mb-8"></div>
            <div className="grid gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Collections
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  Organize and manage your product collections
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {collections.length > 1 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-white/70 backdrop-blur-sm rounded-lg border border-gray-200 text-sm text-gray-600">
                <ArrowUpDown className="w-4 h-4" />
                <span className="hidden sm:inline">Drag to reorder</span>
                <span className="sm:hidden">Drag to reorder</span>
              </div>
            )}
            
            <button
              onClick={handleCreateCollection}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="text-sm sm:text-base">New Collection</span>
            </button>
          </div>
        </div>

        {/* Collections List */}
        {collections.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="p-4 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <Package className="w-10 h-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No collections yet</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Create your first collection to organize products and make them easier for customers to discover
              </p>
              <button
                onClick={handleCreateCollection}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Create Your First Collection
              </button>
            </div>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="collections">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`space-y-6 transition-all duration-200 ${
                    snapshot.isDraggingOver ? 'bg-indigo-50/50 rounded-2xl p-4' : ''
                  }`}
                >
                  {collections.map((collection, index) => (
                    <Draggable key={collection._id} draggableId={collection._id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`group bg-white rounded-2xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md hover:border-gray-200 ${
                            snapshot.isDragging ? 'rotate-1 scale-[1.02] shadow-xl ring-2 ring-indigo-200 ring-opacity-60' : ''
                          } ${isReordering ? 'opacity-60' : ''}`}
                        >
                          {/* Collection Row */}
                          <div className="p-3 sm:p-6">
                            {/* Mobile Layout */}
                            <div className="block sm:hidden">
                              <div className="flex items-start gap-3">
                                {/* Drag Handle & Order */}
                                <div className="flex items-center gap-2">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-all duration-200"
                                    title="Drag to reorder"
                                  >
                                    <GripVertical className="w-4 h-4 text-gray-300" />
                                  </div>
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                    {index + 1}
                                  </span>
                                </div>
                                
                                {/* Collection Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                      <h2 className="text-base font-bold text-gray-900 truncate mb-1">
                                        {collection.name}
                                      </h2>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                          collection.enabled 
                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                                            : 'bg-red-50 text-red-700 border border-red-100'
                                        }`}>
                                          {collection.enabled ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                          <Package className="w-3 h-3" />
                                          <span className="font-medium">{collection.products?.length || 0}</span>
                                        </div>
                                      </div>
                                      {collection.description && (
                                        <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                                          {collection.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Mobile Action Buttons */}
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => handleToggleStatus(collection)}
                                      className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-medium transition-all duration-200 ${
                                        collection.enabled
                                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                                      }`}
                                      title={collection.enabled ? 'Disable' : 'Enable'}
                                    >
                                      {collection.enabled ? (
                                        <Eye className="w-3.5 h-3.5" />
                                      ) : (
                                        <EyeOff className="w-3.5 h-3.5" />
                                      )}
                                    </button>

                                    <button
                                      onClick={() => handleEditCollection(collection._id)}
                                      className="inline-flex items-center justify-center w-8 h-8 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-all duration-200"
                                      title="Edit"
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </button>

                                    <button
                                      onClick={() => handleDelete(collection._id)}
                                      className="inline-flex items-center justify-center w-8 h-8 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-all duration-200"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Desktop Layout */}
                            <div className="hidden sm:flex items-center gap-4">
                              {/* Drag Handle */}
                              <div
                                {...provided.dragHandleProps}
                                className="flex-shrink-0 p-2 rounded-xl hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-all duration-200 hover:scale-110"
                                title="Drag to reorder"
                              >
                                <GripVertical className="w-5 h-5 text-gray-300 group-hover:text-gray-400" />
                              </div>
                              
                              {/* Order Number */}
                              <div className="flex-shrink-0">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                  {index + 1}
                                </span>
                              </div>
                              
                              {/* Collection Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                                    {collection.name}
                                  </h2>
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    collection.enabled 
                                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                                      : 'bg-red-50 text-red-700 border border-red-100'
                                  }`}>
                                    {collection.enabled ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <div className="flex items-center gap-1.5">
                                    <Package className="w-4 h-4" />
                                    <span className="font-medium">{collection.products?.length || 0}</span>
                                    <span>products</span>
                                  </div>
                                  {collection.description && (
                                    <>
                                      <span>•</span>
                                      <p className="truncate max-w-xs sm:max-w-md">{collection.description}</p>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Desktop Action Buttons */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleStatus(collection)}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                                    collection.enabled
                                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }`}
                                >
                                  {collection.enabled ? (
                                    <Eye className="w-4 h-4" />
                                  ) : (
                                    <EyeOff className="w-4 h-4" />
                                  )}
                                  <span className="hidden md:inline">
                                    {collection.enabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                </button>

                                <button
                                  onClick={() => handleEditCollection(collection._id)}
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-xl hover:bg-indigo-200 transition-all duration-200 hover:scale-105 active:scale-95"
                                >
                                  <Edit className="w-4 h-4" />
                                  <span className="hidden md:inline">Edit</span>
                                </button>

                                <button
                                  onClick={() => handleDelete(collection._id)}
                                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-xl hover:bg-red-200 transition-all duration-200 hover:scale-105 active:scale-95"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span className="hidden md:inline">Delete</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default CollectionsManagement; 